import React, { useState, useEffect } from "react";
import {
  FileText,
  Camera,
  ArrowRight,
  ArrowLeft,
  Check,
  Loader,
  AlertCircle,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import ExtendedWarrantyService from "../../lib/ExtendedWarrantyService";
import ExtendedWarrantyScreenWrapper, {
  ExtendedWarrantyHeader,
  ExtendedWarrantyCard,
} from "./ExtendedWarrantyScreenWrapper";

const ExtendedWarrantyChoiceScreen = ({
  onPolicyUpload,
  onMileageCheckin,
  onBack,
  onComplete, // Called when user already has a policy checkin and wants to continue
  choiceOptions = null,
}) => {
  const { user, getIdToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flowData, setFlowData] = useState(null);
  const [existingPolicyCheckin, setExistingPolicyCheckin] = useState(null);

  // Single effect to load flow data and determine state
  useEffect(() => {
    const loadFlowData = async () => {
      try {
        // Wait for user authentication
        if (!user) {
          setLoading(false);
          return;
        }

        setLoading(true);
        setError(null);

        const token = await getIdToken();
        if (!token) {
          throw new Error("Authentication required");
        }

        // Get the flow data (single API call)
        const flow = await ExtendedWarrantyService.getFlow(token);
        console.error(flow);
        setFlowData(flow);

        if (flow) {
          // Check if flow has existing policy checkin
          if (ExtendedWarrantyService.hasExistingPolicyCheckin(flow)) {
            const policyCheckin = ExtendedWarrantyService.getPolicyCheckinFromFlow(
              flow
            );
            setExistingPolicyCheckin(policyCheckin);

            // Store in session storage for other components
            sessionStorage.setItem(
              "extendedWarrantyFlowData",
              JSON.stringify({
                flowId: flow.id,
                hasExistingPolicyCheckin: true,
                policyCheckin: policyCheckin,
                timestamp: new Date().toISOString(),
              })
            );
          } else {
            // Flow exists but no policy checkin yet
            sessionStorage.setItem(
              "extendedWarrantyFlowData",
              JSON.stringify({
                flowId: flow.id,
                hasExistingPolicyCheckin: false,
                timestamp: new Date().toISOString(),
              })
            );
          }
        }

        setLoading(false);
      } catch (err) {
        console.error("Error loading flow data:", err);
        setError(
          "Unable to load extended warranty information. Please try again."
        );
        setLoading(false);
      }
    };

    loadFlowData();
  }, [user, getIdToken]);

  // Default choice options
  const defaultOptions = [
    {
      id: "policy_upload",
      value: "policy_upload",
      text: "Upload Policy Document",
      description: "Upload your extended warranty policy declaration",
      icon: <FileText className="w-6 h-6 text-blue-600" />,
      iconBg: "bg-blue-100",
      features: ["Quick and secure", "PDF or image formats accepted"],
      displayOrder: 1,
      onSelect: onPolicyUpload,
    }
  ];

  const options = choiceOptions || defaultOptions;
  const sortedOptions = [...options].sort(
    (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
  );

  const handleOptionSelect = (option) => {
    if (option.onSelect) {
      option.onSelect();
    } else {
      // Fallback
      if (option.value === "policy_upload" && onPolicyUpload) {
        onPolicyUpload();
      } else if (option.value === "mileage_checkin" && onMileageCheckin) {
        onMileageCheckin();
      }
    }
  };

  const handleContinue = () => {
    if (onComplete) {
      onComplete();
    }
  };

  // Loading state
  if (loading) {
    return (
      <ExtendedWarrantyScreenWrapper>
        <div className="flex flex-col items-center justify-center p-8 min-h-64">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader className="w-8 h-8 text-blue-500 animate-pulse" />
            </div>
          </div>
          <span className="mt-4 text-gray-600 text-center">
            Loading extended warranty information...
          </span>
        </div>
      </ExtendedWarrantyScreenWrapper>
    );
  }

  // Error state
  if (error) {
    return (
      <ExtendedWarrantyScreenWrapper>
        {onBack && (
          <button
            onClick={onBack}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </button>
        )}

        <div className="text-center space-y-4">
          <div className="flex items-center justify-center text-red-600">
            <AlertCircle className="w-6 h-6 mr-2" />
            <span className="text-lg font-semibold">Something Went Wrong</span>
          </div>
          <p className="text-gray-600">{error}</p>
        </div>

        <ExtendedWarrantyCard>
          <div className="p-6">
            <button
              onClick={() => window.location.reload()}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </ExtendedWarrantyCard>
      </ExtendedWarrantyScreenWrapper>
    );
  }

  // Show existing policy checkin if found
  if (existingPolicyCheckin) {
    const checkinDate = existingPolicyCheckin.checkedInAt
      ? new Date(existingPolicyCheckin.checkedInAt).toLocaleDateString()
      : "recently";

    return (
      <ExtendedWarrantyScreenWrapper>
        {onBack && (
          <button
            onClick={onBack}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </button>
        )}

        <ExtendedWarrantyHeader
          title="Extended Warranty Policy Found"
          subtitle="We already have your extended warranty policy information on file"
        />

        <ExtendedWarrantyCard className="bg-green-50 border-green-100">
          <div className="p-6">
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-green-500 mt-1" />
              <div>
                <p className="font-medium text-green-900">
                  Policy Document Submitted
                </p>
                <p className="text-sm text-green-700 mt-1">
                  Status: {existingPolicyCheckin.status || "Processing"}
                </p>
                <p className="text-sm text-green-700">
                  Submitted on {checkinDate}
                </p>
                {existingPolicyCheckin.files &&
                  existingPolicyCheckin.files.length > 0 && (
                    <p className="text-xs text-green-700 mt-2">
                      {existingPolicyCheckin.files.length} file(s) uploaded
                    </p>
                  )}
              </div>
            </div>
          </div>
        </ExtendedWarrantyCard>

        <ExtendedWarrantyCard>
          <div className="p-6">
            <button
              onClick={handleContinue}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Continue
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </ExtendedWarrantyCard>

        {/* Optional: Allow new submission */}
        <div className="space-y-2">
          <p className="text-sm text-center text-gray-600">
            Need to submit a new policy?
          </p>
          <div className="flex justify-center space-x-4">
            {onPolicyUpload && (
              <button
                onClick={onPolicyUpload}
                className="text-blue-600 text-sm hover:text-blue-800"
              >
                Upload New Policy
              </button>
            )}
            {onMileageCheckin && (
              <button
                onClick={onMileageCheckin}
                className="text-blue-600 text-sm hover:text-blue-800"
              >
                Mileage Check-In Instead
              </button>
            )}
          </div>
        </div>
      </ExtendedWarrantyScreenWrapper>
    );
  }

  // Show choice options (no existing policy checkin found)
  return (
    <ExtendedWarrantyScreenWrapper>
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </button>
      )}

      <ExtendedWarrantyHeader
        title="Choose Verification Method"
        subtitle="Select how you'd like to verify your extended warranty information"
      />

      {sortedOptions.map((option, index) => (
        <ExtendedWarrantyCard
          key={option.id || index}
          className="cursor-pointer hover:shadow-md transition-shadow"
        >
          <div
            onClick={() => handleOptionSelect(option)}
            className="p-6 space-y-4"
          >
            <div className="flex items-start gap-4">
              <div
                className={`w-12 h-12 ${
                  option.iconBg || "bg-gray-100"
                } rounded-full flex items-center justify-center flex-shrink-0`}
              >
                {option.icon || <FileText className="w-6 h-6 text-gray-600" />}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">
                  {option.text}
                </h3>
                <p className="text-gray-600 mt-1">{option.description}</p>
                {option.features && option.features.length > 0 && (
                  <div className="mt-3 space-y-1">
                    {option.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="text-sm text-gray-500">
                        ✓ {feature}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <ArrowRight className="w-5 h-5 text-gray-400 mt-1" />
            </div>
          </div>
        </ExtendedWarrantyCard>
      ))}

      <p className="text-xs text-center text-gray-500 mt-6">
        Your information is securely processed and encrypted using bank-level
        security protocols
      </p>
    </ExtendedWarrantyScreenWrapper>
  );
};

export default ExtendedWarrantyChoiceScreen;
